"use client"

import { useEffect, useMemo, useState } from "react"
import { Star, Users, Clock, Award, BookOpen, Play, ChevronDown, Loader2, Send, Edit2, Trash2, X, MessageCircle } from "lucide-react"
import { doc, getDoc, collection, addDoc, onSnapshot, query, serverTimestamp, updateDoc, deleteDoc, where, limit, getDocs } from "firebase/firestore"
import { db, auth } from "@/lib/firebase"
import { useAuth } from "@/hooks/use-auth"
import { toast } from "sonner"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"
import EnrollButton from "@/components/courses/enroll-button"
import BackButton from "@/components/ui/back-button"

const CourseDetail = ({ courseId }: { courseId: string }) => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState("overview")
  const [expandedModule, setExpandedModule] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [course, setCourse] = useState<any | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [resolvedId, setResolvedId] = useState<string | null>(null)

  // Real-time Lists
  const [reviews, setReviews] = useState<any[]>([])
  const [faqs, setFaqs] = useState<any[]>([])

  // Submission States
  const [reviewText, setReviewText] = useState("")
  const [ratingInput, setRatingInput] = useState(5)
  const [isSubmittingReview, setIsSubmittingReview] = useState(false)

  const [faqQuestion, setFaqQuestion] = useState("")
  const [isSubmittingFaq, setIsSubmittingFaq] = useState(false)

  // Edit States
  const [editingReviewId, setEditingReviewId] = useState<string | null>(null)
  const [editReviewText, setEditReviewText] = useState("")
  const [editRatingInput, setEditRatingInput] = useState(5)

  const [editingFaqId, setEditingFaqId] = useState<string | null>(null)
  const [editFaqQuestion, setEditFaqQuestion] = useState("")

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Try fetching by ID first
        let snap = await getDoc(doc(db, "courses", courseId))
        let courseData = null

        if (snap.exists()) {
          courseData = { id: snap.id, ...(snap.data() as any) }
        } else {
          // If not found by ID, try fetching by slug
          const q = query(collection(db, "courses"), where("slug", "==", courseId), limit(1))
          const slugSnap = await getDocs(q)
          if (!slugSnap.empty) {
            const doc = slugSnap.docs[0]
            courseData = { id: doc.id, ...(doc.data() as any) }
          }
        }

        if (!courseData) {
          setCourse(null)
          setError("Course not found")
          return
        }

        setCourse(courseData)
        setResolvedId(courseData.id)
      } catch (e: any) {
        console.error(e)
        setError(e?.message || "Failed to load course")
      } finally {
        setLoading(false)
      }
    }

    fetchCourse()
  }, [courseId])

  useEffect(() => {
    if (!resolvedId) return

    const qReviews = query(collection(db, "courses", resolvedId, "reviews"))
    const unsubReviews = onSnapshot(qReviews, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      // Sort client-side to avoid index requirement
      const sorted = data.sort((a: any, b: any) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      // Filter by approved AND isVisible
      setReviews(sorted.filter((r: any) => r.approved !== false && r.isVisible !== false))
    })

    const qFaqs = query(collection(db, "courses", resolvedId, "faqs"))
    const unsubFaqs = onSnapshot(qFaqs, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      const sorted = data.sort((a: any, b: any) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      setFaqs(sorted.filter((f: any) => f.approved !== false && f.isVisible !== false))
    })

    return () => {
       unsubReviews()
       unsubFaqs()
    }
  }, [resolvedId])

  // Review Handlers
  const submitReview = async (e: React.FormEvent) => {
    e.preventDefault()

    // 🔍 Debugging Auth State
    console.log("Hooks User State:", user)
    console.log("Firebase Auth currentUser:", auth.currentUser)

    if (!auth.currentUser) return toast.error("Session expired. Please log in again.")
    if (!reviewText.trim()) return

    // Use hook user as fallback for name if auth state is still syncing
    const activeName = auth.currentUser.displayName || user?.displayName || "Unknown Scholar"

    setIsSubmittingReview(true)
    try {
      await addDoc(collection(db, "courses", courseId, "reviews"), {
         userId: auth.currentUser.uid,
         userName: activeName,
         text: reviewText,
         rating: ratingInput,
         approved: true, // Auto-publishing for now; set to false if moderation is needed
         createdAt: serverTimestamp()
      })
      toast.success("Review posted successfully!")
      setReviewText("")
      setRatingInput(5)
    } catch (err) {
      console.error("Error posting review:", err)
      toast.error("Failed to post review. Check console for details.")
    } finally {
      setIsSubmittingReview(false)
    }
  }

  const updateReview = async (reviewId: string) => {
     try {
        await updateDoc(doc(db, "courses", courseId, "reviews", reviewId), {
           text: editReviewText,
           rating: editRatingInput
        })
        setEditingReviewId(null)
        toast.success("Review updated.")
     } catch (e) {
        toast.error("Failed to update review.")
     }
  }

  const deleteReview = async (reviewId: string) => {
     if(!window.confirm("Permanently delete this review?")) return;
     try {
        await deleteDoc(doc(db, "courses", courseId, "reviews", reviewId))
        toast.success("Review deleted.")
     } catch (e) {
        toast.error("Failed to delete review.")
     }
  }

  // FAQ Handlers
  const submitFaq = async (e: React.FormEvent) => {
    e.preventDefault()

    // 🔍 Debugging Auth State
    console.log("Firebase Auth currentUser (FAQ):", auth.currentUser)

    if (!auth.currentUser) return toast.error("Session expired. Please log in again.")
    if (!faqQuestion.trim()) return

    // Use hook user as fallback for name if auth state is still syncing
    const activeName = auth.currentUser.displayName || user?.displayName || "Unknown Scholar"

    setIsSubmittingFaq(true)
    try {
      await addDoc(collection(db, "courses", courseId, "faqs"), {
         userId: auth.currentUser.uid,
         userName: activeName,
         question: faqQuestion,
         answer: null,
         approved: true, // Auto-publishing for now; set to false if moderation is needed
         createdAt: serverTimestamp()
      })
      toast.success("Question submitted! An instructor will answer shortly.")
      setFaqQuestion("")
    } catch (err) {
      console.error("Error submitting question:", err)
      toast.error("Failed to submit question. Check console for details.")
    } finally {
      setIsSubmittingFaq(false)
    }
  }

  const updateFaq = async (faqId: string) => {
     try {
        await updateDoc(doc(db, "courses", courseId, "faqs", faqId), {
           question: editFaqQuestion
        })
        setEditingFaqId(null)
        toast.success("Question updated.")
     } catch (e) {
        toast.error("Failed to update question.")
     }
  }

  const deleteFaq = async (faqId: string) => {
     if(!window.confirm("Permanently delete this question?")) return;
     try {
        await deleteDoc(doc(db, "courses", courseId, "faqs", faqId))
        toast.success("Question deleted.")
     } catch (e) {
        toast.error("Failed to delete question.")
     }
  }

  const tabs = useMemo(
    () => [
      { id: "overview", label: "Overview" },
      { id: "curriculum", label: "Curriculum" },
      { id: "reviews", label: "Reviews" },
      { id: "faqs", label: "FAQs" },
    ],
    []
  )

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#b48c36]" size={40} />
      </div>
    )
  }

  if (error || !course) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <div className="bg-slate-950 text-white rounded-sm border border-slate-800 p-6">
          <h1 className="text-xl font-bold font-serif mb-2 text-[#b48c36]">Course Unavailable</h1>
          <p className="text-sm text-slate-400">{error || "Course not found"}</p>
        </div>
      </div>
    )
  }

  const highlights: string[] =
    Array.isArray(course.highlights) && course.highlights.length > 0
      ? course.highlights
      : [
          "Live interactive classes",
          "Recorded sessions access",
          "Doubt resolution support",
          "Tests & practice material",
        ]

  const modules: any[] = Array.isArray(course.modules) ? course.modules : []
  const avgRating = reviews.length > 0 ? (reviews.reduce((acc, curr) => acc + curr.rating, 0) / reviews.length).toFixed(1) : (course.rating || "New")

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Cinematic Hero Section */}
      <section className="bg-slate-950 pt-16 sm:pt-24 pb-6 sm:pb-8 relative overflow-hidden border-b border-slate-900">
        
        {/* Course Thumbnail as Dark Background */}
        {(() => {
          const img = course.image || course.imageUrl;
          const isDummy = !img || img.includes("unsplash.com") || img.includes("placeholder.svg");
          if (isDummy) return null;

          return (
            <div 
              className="absolute inset-0 z-0 opacity-[0.12]" 
              style={{ backgroundImage: `url(${img})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(6px)' }} 
            />
          );
        })()}
        <div className="absolute inset-0 z-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/40" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-30">
          <div className="mb-4 sm:mb-6">
            <BackButton className="!bg-white/10 !text-white !border-white/20 hover:!bg-[#b48c36] hover:!border-[#b48c36] relative z-50" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left: Content block */}
            <div className="lg:col-span-7 space-y-4 lg:space-y-6">
              <div>
                <span className="text-[8px] sm:text-[9px] font-bold text-[#b48c36] uppercase tracking-[0.3em] mb-1 sm:mb-2 block">{course.categoryName || course.category || "Course"}</span>
                <h1 className="text-3xl sm:text-5xl font-serif font-medium text-white leading-tight">{course.title}</h1>
              </div>

              <p className="text-sm sm:text-base text-slate-300 leading-relaxed font-light line-clamp-3 sm:line-clamp-none">{course.description}</p>

              {/* Compact Meta Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 py-3 sm:py-4 border-y border-slate-800">
                <div className="flex items-center gap-2">
                  <Star size={14} className="fill-[#b48c36] text-[#b48c36] shrink-0" />
                  <div>
                     <span className="text-xs sm:text-sm font-bold text-white block">{avgRating}</span>
                     <span className="text-[8px] uppercase tracking-widest text-slate-500">{reviews.length} reviews</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Users size={14} className="text-[#b48c36] shrink-0" />
                  <div>
                     <span className="text-xs sm:text-sm font-bold text-white block">{typeof course.students === "number" ? course.students.toLocaleString() : "—"}</span>
                     <span className="text-[8px] uppercase tracking-widest text-slate-500">enrolled</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-[#b48c36] shrink-0" />
                  <div>
                     <span className="text-xs sm:text-sm font-bold text-white block">{course.duration || "—"}</span>
                     <span className="text-[8px] uppercase tracking-widest text-slate-500">duration</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Award size={14} className="text-[#b48c36] shrink-0" />
                  <div>
                     <span className="text-xs sm:text-sm font-bold text-white block">92%</span>
                     <span className="text-[8px] uppercase tracking-widest text-slate-500">success</span>
                  </div>
                </div>
              </div>
              
              {/* Instructor Mini Box & Actions */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-1">
                 <div className="flex items-center gap-3">
                   <div className="w-10 h-10 bg-slate-900 border border-slate-800 flex items-center justify-center shrink-0">
                     <span className="text-xs font-bold text-[#b48c36] font-serif">
                       {(String(course.instructor || "RA")
                         .split(" ")
                         .filter(Boolean)
                         .slice(0, 2)
                         .map((p: string) => p[0]?.toUpperCase())
                         .join("") || "RA")}
                     </span>
                   </div>
                   <div>
                     <p className="font-medium text-white text-sm font-serif">{course.instructor}</p>
                     <p className="text-[9px] text-slate-500 uppercase tracking-widest">Lead Instructor</p>
                   </div>
                 </div>

                 {/* Compact Action Buttons */}
                 <div className="flex items-center gap-3 w-full sm:w-auto mt-2 sm:mt-0">
                    <EnrollButton
                      course={{
                        ...course,
                        fees: course.fees || course.discountedPrice || 0,
                      }}
                      size="md"
                    />
                 </div>
              </div>
            </div>

            {/* Right: Floating Image Card (Responsive) */}
            <div className="lg:col-span-5 order-first lg:order-last">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="relative group"
              >
                {/* Decorative glow behind card */}
                <div className="absolute -inset-1 bg-gradient-to-r from-[#b48c36]/20 to-blue-500/20 rounded-2xl blur-2xl opacity-50 group-hover:opacity-75 transition-opacity" />
                
                <div className="relative bg-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
                  {(() => {
                    const img = course.image || course.imageUrl;
                    const isDummy = !img || img.includes("unsplash.com") || img.includes("placeholder.svg");
                    
                    if (!isDummy) {
                      return (
                        <div className="aspect-[16/10] overflow-hidden">
                          <img 
                            src={img} 
                            alt={course.title} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          />
                        </div>
                      );
                    }

                    // Institutional Wireframe Placeholder
                    return (
                      <div className="aspect-[16/10] bg-slate-950 flex flex-col items-center justify-center relative overflow-hidden">
                        <div className="absolute inset-0 opacity-[0.03] bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />
                        <div className="w-20 h-20 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center text-[#b48c36] shadow-inner mb-4">
                          <BookOpen size={40} strokeWidth={1} />
                        </div>
                        <p className="text-[10px] font-black text-white/20 uppercase tracking-[0.5em]">Rohith's Academy</p>
                      </div>
                    );
                  })()}

                  {/* Card Bottom Accent */}
                  <div className="h-1.5 w-full bg-gradient-to-r from-transparent via-[#b48c36] to-transparent opacity-30" />
                </div>
              </motion.div>
            </div>

          </div>
        </div>
      </section>

      {/* Tabs Section - Tighter Padding */}
      <section className="border-b border-slate-200 sticky top-0 z-20 bg-white/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-6 overflow-x-auto no-scrollbar">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3.5 px-2 font-bold text-[10px] uppercase tracking-[0.1em] border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id
                  ? "text-[#b48c36] border-[#b48c36]"
                  : "text-slate-500 border-transparent hover:text-slate-900"
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content - Compact Spacing */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mb-20">
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 space-y-8">
              <div>
                <h3 className="text-xl font-serif font-medium text-slate-900 mb-3 flex items-center gap-3">
                   <span className="w-6 h-px bg-[#b48c36]"></span> Abstract
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">{course.description}</p>
              </div>

              <div>
                <h3 className="text-xl font-serif font-medium text-slate-900 mb-4 flex items-center gap-3">
                   <span className="w-6 h-px bg-[#b48c36]"></span> Key Takeaways
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {highlights.slice(0, 6).map((item, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-sm bg-white border border-slate-200">
                      <div className="w-4 h-4 rounded-full bg-slate-900 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-[10px] text-[#b48c36] font-bold">✓</span>
                      </div>
                      <p className="text-slate-700 text-xs font-medium">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white rounded-sm border border-slate-200 p-6 sticky top-24">
                <h4 className="font-bold text-slate-900 mb-4 text-[10px] uppercase tracking-[0.2em] border-b border-slate-100 pb-3">Course Protocol</h4>
                <div className="space-y-3.5">
                  {highlights.map((highlight, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <span className="text-[#b48c36] font-bold text-xs mt-0.5">■</span>
                      <p className="text-xs text-slate-600 font-medium leading-relaxed">{highlight}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "curriculum" && (
          <div className="max-w-3xl">
            <h3 className="text-xl font-serif font-medium text-slate-900 mb-5 flex items-center gap-3">
              <span className="w-6 h-px bg-[#b48c36]"></span> Syllabus
            </h3>
            <div className="space-y-3">
              {modules.length === 0 ? (
                <div className="bg-white border border-slate-200 p-6 text-center text-slate-500 text-sm">
                  The syllabus is currently pending publication.
                </div>
              ) : modules.map((module) => (
                <div key={module.id} className="border border-slate-200 bg-white">
                  <button
                    onClick={() => setExpandedModule(expandedModule === module.id ? null : module.id)}
                    className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4 text-left">
                      <div className="w-8 h-8 bg-slate-900 text-[#b48c36] flex items-center justify-center shrink-0">
                         <BookOpen size={14} />
                      </div>
                      <div>
                         <h4 className="font-bold text-slate-900 text-sm">{module.title}</h4>
                      </div>
                    </div>
                    <ChevronDown size={16} className={`text-slate-400 transition-transform ${expandedModule === module.id ? "rotate-180" : ""}`} />
                  </button>

                  {expandedModule === module.id && (
                    <div className="border-t border-slate-100 bg-slate-50/50 p-4 space-y-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-white border border-slate-200 hover:border-[#b48c36] transition-colors group cursor-pointer">
                          <Play size={12} className="text-slate-400 group-hover:text-[#b48c36]" />
                          <span className="text-xs font-semibold text-slate-700">Lesson {i + 1}: Conceptual Overview</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "reviews" && (
          <div className="max-w-3xl">
            <h3 className="text-xl font-serif font-medium text-slate-900 mb-5 flex items-center gap-3">
              <span className="w-6 h-px bg-[#b48c36]"></span> Student Testimonials
            </h3>
            
            {/* Form */}
            <div className="bg-slate-950 p-6 mb-8 relative overflow-hidden">
               <div className="absolute top-0 right-0 w-24 h-24 bg-[#b48c36]/10 rounded-full blur-2xl"></div>
               {user ? (
                 <form onSubmit={submitReview} className="space-y-4 relative z-10">
                    <div className="flex flex-col sm:flex-row gap-4">
                       <div>
                          <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-2 block">Rating</label>
                          <div className="flex gap-1.5">
                             {[1,2,3,4,5].map((star) => (
                               <button type="button" key={star} onClick={() => setRatingInput(star)} className={`p-2 transition-colors border ${ratingInput >= star ? "bg-white border-white text-slate-950" : "bg-slate-900 border-slate-800 text-slate-600"}`}>
                                  <Star size={14} className={ratingInput >= star ? "fill-[#b48c36] text-[#b48c36]" : ""} />
                               </button>
                             ))}
                          </div>
                       </div>
                       <div className="flex-1">
                          <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-2 block">Feedback</label>
                          <textarea rows={2} required value={reviewText} onChange={(e) => setReviewText(e.target.value)} className="w-full bg-slate-900 border border-slate-800 p-3 text-xs text-white focus:outline-none focus:border-[#b48c36]" placeholder="Detail your experience..."></textarea>
                       </div>
                    </div>
                    <div className="flex justify-end mt-2">
                       <button disabled={isSubmittingReview} className="bg-[#b48c36] text-white px-6 py-3 font-bold text-[9px] uppercase tracking-[0.2em] hover:bg-white hover:text-slate-950 w-full sm:w-auto">
                          {isSubmittingReview ? "Posting..." : "Publish Review"}
                       </button>
                    </div>
                 </form>
               ) : (
                 <div className="text-center py-4 relative z-10">
                    <p className="text-sm text-slate-300 font-serif mb-3">Identity authentication required.</p>
                    <Link to="/login" className="bg-[#b48c36] text-white font-bold px-6 py-2.5 text-[9px] uppercase tracking-[0.2em] inline-block">Sign In</Link>
                 </div>
               )}
            </div>

            {/* List */}
            <div className="space-y-4">
              {reviews.length === 0 ? (
                 <div className="text-center py-10 bg-white border border-slate-200">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">No evaluations yet.</p>
                 </div>
              ) : reviews.map((review) => (
                <div key={review.id} className="bg-white border border-slate-200 p-5">
                  {editingReviewId === review.id ? (
                     <div className="space-y-3">
                        <div className="flex gap-1.5 mb-2">
                           {[1,2,3,4,5].map((star) => (
                             <button type="button" key={star} onClick={() => setEditRatingInput(star)} className={`p-1.5 border ${editRatingInput >= star ? "bg-slate-950 border-slate-950" : "bg-slate-50 border-slate-200"}`}>
                                <Star size={12} className={editRatingInput >= star ? "fill-[#b48c36] text-[#b48c36]" : "text-slate-400"} />
                             </button>
                           ))}
                        </div>
                        <textarea rows={2} value={editReviewText} onChange={(e) => setEditReviewText(e.target.value)} className="w-full border border-slate-300 p-3 text-xs bg-slate-50"></textarea>
                        <div className="flex gap-2">
                           <button onClick={() => updateReview(review.id)} className="bg-slate-950 text-white px-4 py-2 text-[9px] uppercase tracking-widest font-bold">Update</button>
                           <button onClick={() => setEditingReviewId(null)} className="border border-slate-200 px-4 py-2 text-[9px] uppercase tracking-widest font-bold">Cancel</button>
                        </div>
                     </div>
                  ) : (
                     <>
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-slate-900 shrink-0 flex items-center justify-center">
                              <span className="text-xs font-bold text-[#b48c36] font-serif">{review.userName.charAt(0).toUpperCase()}</span>
                            </div>
                            <div>
                              <p className="font-bold text-slate-900 text-sm font-serif">{review.userName}</p>
                            </div>
                          </div>
                          <div className="flex gap-0.5">
                            {Array.from({ length: 5 }).map((_, j) => (
                              <Star key={j} size={12} className={j < review.rating ? "fill-[#b48c36] text-[#b48c36]" : "text-slate-200"} />
                            ))}
                          </div>
                        </div>
                        <p className="text-slate-600 text-xs leading-relaxed">"{review.text}"</p>

                         {/* Admin Reply */}
                         {review.adminReply && (
                           <div className="mt-4 bg-amber-50 border-l-2 border-[#b48c36] p-4">
                             <div className="flex items-center gap-2 mb-1.5">
                               <MessageCircle size={11} className="text-[#b48c36]" />
                               <span className="text-[9px] font-bold text-[#b48c36] uppercase tracking-widest">Institution Response</span>
                             </div>
                             <p className="text-xs text-slate-700 leading-relaxed">{review.adminReply}</p>
                           </div>
                         )}
                        
                        {user?.uid === review.userId && (
                           <div className="flex gap-4 mt-4 pt-4 border-t border-slate-100">
                              <button onClick={() => { setEditingReviewId(review.id); setEditReviewText(review.text); setEditRatingInput(review.rating); }} className="text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 flex items-center gap-1.5"><Edit2 size={10} /> Edit</button>
                              <button onClick={() => deleteReview(review.id)} className="text-[9px] font-bold uppercase tracking-widest text-red-500 hover:text-red-700 flex items-center gap-1.5"><Trash2 size={10} /> Delete</button>
                           </div>
                        )}
                     </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "faqs" && (
          <div className="max-w-3xl">
            <h3 className="text-xl font-serif font-medium text-slate-900 mb-5 flex items-center gap-3">
              <span className="w-6 h-px bg-[#b48c36]"></span> Public Inquiries
            </h3>
            
            {/* Form */}
            <div className="bg-slate-950 p-6 mb-8 relative border-l-4 border-[#b48c36]">
               {user ? (
                 <form onSubmit={submitFaq} className="flex flex-col sm:flex-row gap-3">
                    <input type="text" required value={faqQuestion} onChange={(e) => setFaqQuestion(e.target.value)} placeholder="Ask the instructor..." className="flex-1 bg-slate-900 border border-slate-800 p-3 text-xs text-white focus:outline-none" />
                    <button disabled={isSubmittingFaq} className="bg-[#b48c36] text-white px-6 py-3 font-bold text-[9px] uppercase tracking-[0.2em] whitespace-nowrap">
                       {isSubmittingFaq ? "Wait..." : "Ask"}
                    </button>
                 </form>
               ) : (
                 <div className="flex items-center justify-between gap-4">
                    <p className="text-xs text-slate-300">Sign in to ask questions.</p>
                    <Link to="/login" className="bg-white text-slate-950 font-bold px-5 py-2 text-[9px] uppercase tracking-widest">Sign In</Link>
                 </div>
               )}
            </div>

            {/* List */}
            <div className="space-y-4">
              {faqs.length === 0 ? (
                 <div className="text-center py-10 bg-white border border-slate-200 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    No inquiries yet.
                 </div>
              ) : faqs.map((faq) => (
                <div key={faq.id} className="bg-white border border-slate-200 p-5">
                  {editingFaqId === faq.id ? (
                     <div className="space-y-3">
                        <input type="text" value={editFaqQuestion} onChange={(e) => setEditFaqQuestion(e.target.value)} className="w-full border border-slate-300 p-3 text-xs bg-slate-50"/>
                        <div className="flex gap-2">
                           <button onClick={() => updateFaq(faq.id)} className="bg-slate-950 text-white px-4 py-2 text-[9px] uppercase tracking-widest font-bold">Update</button>
                           <button onClick={() => setEditingFaqId(null)} className="border border-slate-200 px-4 py-2 text-[9px] uppercase tracking-widest font-bold">Cancel</button>
                        </div>
                     </div>
                  ) : (
                     <>
                        <div className="flex gap-3 items-start">
                           <span className="w-6 h-6 shrink-0 bg-slate-100 text-slate-500 flex items-center justify-center text-[10px] font-bold">Q</span>
                           <div>
                              <p className="font-bold text-slate-900 text-sm mb-1">{faq.question}</p>
                              <p className="text-[11px] font-bold text-[#b48c36] uppercase tracking-widest">Asked by: {faq.userName || "Scholar"}</p>
                           </div>
                        </div>
                        
                        {faq.answer ? (
                           <div className="flex gap-3 items-start mt-4 pt-4 border-t border-slate-100">
                              <span className="w-6 h-6 shrink-0 bg-[#b48c36]/10 text-[#b48c36] flex items-center justify-center text-[10px] font-bold">A</span>
                              <div>
                                 <p className="text-slate-600 text-xs leading-relaxed">{faq.answer}</p>
                                 <p className="text-[8px] font-bold text-[#b48c36] mt-1.5 uppercase tracking-widest">{course.instructor || "Instructor"} Response</p>
                              </div>
                           </div>
                        ) : (
                           <div className="mt-4 border-t border-slate-50 pt-3">
                              <span className="bg-slate-50 text-slate-400 text-[9px] font-bold px-2 py-1 uppercase tracking-widest">Awaiting Response</span>
                           </div>
                        )}

                        {user?.uid === faq.userId && (
                           <div className="flex gap-4 mt-4 pt-4 border-t border-slate-100">
                              <button onClick={() => { setEditingFaqId(faq.id); setEditFaqQuestion(faq.question); }} className="text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 flex items-center gap-1.5"><Edit2 size={10} /> Edit</button>
                              <button onClick={() => deleteFaq(faq.id)} className="text-[9px] font-bold uppercase tracking-widest text-red-500 hover:text-red-700 flex items-center gap-1.5"><X size={10} /> Delete</button>
                           </div>
                        )}
                     </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

export default CourseDetail

